import {
  UpcomingBenefitCreditsDocument,
  useRecordBenefitCreditMutation,
  useUpcomingBenefitCreditsQuery,
  type UpcomingBenefitCreditsQuery,
} from '@transactions/graphql';
import {CircleDollarSign} from 'lucide-react';
import * as React from 'react';
import {Button} from 'src/Button';
import {
  formatCents,
  formatPeriodLabel,
} from 'src/CardBenefitsView/benefitUtils';

type CreditRow = UpcomingBenefitCreditsQuery['upcomingBenefitCredits'][number];

const CreditRowItem = ({row, onRecorded}: {row: CreditRow; onRecorded: () => void}) => {
  const [error, setError] = React.useState<string | null>(null);
  const [recordCredit, {loading}] = useRecordBenefitCreditMutation();

  const handleRecord = async () => {
    setError(null);
    try {
      await recordCredit({
        variables: {benefitId: row.benefitId},
        refetchQueries: [{query: UpcomingBenefitCreditsDocument}],
      });
      onRecorded();
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const err = e as Error;
      setError(err.message);
    }
  };

  return (
    <div className="border-border flex items-center gap-3 px-4 py-2.5 not-last:border-b">
      <CircleDollarSign className="text-muted-foreground size-4 shrink-0" />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="flex min-w-0 items-baseline gap-1.5">
          <span className="truncate text-sm font-medium">{row.benefitName}</span>
          {row.payee && (
            <span className="text-muted-foreground truncate text-xs">
              {row.payee}
            </span>
          )}
        </span>
        <span className="text-muted-foreground text-xs">
          {formatPeriodLabel(row.startDate, row.endDate)}
          {` · loads to ${row.creditAccountName}`}
        </span>
        {error && <span className="text-destructive text-xs">{error}</span>}
      </div>
      <Button
        variant="outline"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick={handleRecord}
        disabled={loading}
        className="shrink-0"
      >
        {loading
          ? 'Recording...'
          : `Record ${formatCents(row.remainingAmount)}`}
      </Button>
    </div>
  );
};

/**
 * Current, not-yet-recorded periods of benefits that load as credits onto a
 * separate account (creditAccountId set). Exposed as a hook so the Inbox can
 * include the rows in its empty-state decision.
 *
 * Server-computed (UpcomingBenefitCredits, soonest-expiring first) — this
 * used to derive the rows from AllFeePeriods, which made every inbox visit
 * resolve usage for every period of every benefit ever, even to render
 * nothing.
 */
export const useBenefitCreditRows = () => {
  const {data, refetch} = useUpcomingBenefitCreditsQuery({
    fetchPolicy: 'cache-and-network',
  });
  const rows: CreditRow[] = data?.upcomingBenefitCredits ?? [];
  return {rows, refetch};
};

/**
 * Inbox rows for the credits from useBenefitCreditRows: each gets a
 * one-click "Record" button that creates and links the credit transaction.
 */
export const UpcomingBenefitCredits = ({
  rows,
  onRecorded,
  hideHeading = false,
}: {
  rows: CreditRow[];
  onRecorded: () => void;
  // The inbox card supplies its own header
  hideHeading?: boolean;
}) => {
  if (rows.length === 0) return null;

  return (
    <div>
      {!hideHeading && (
        <div className="bg-muted border-border text-muted-foreground border-b px-4 py-2 text-sm font-medium">
          Benefit credits to record ({rows.length})
        </div>
      )}
      {rows.map((row) => (
        <CreditRowItem
          key={`${row.benefitId}-${row.startDate}`}
          row={row}
          onRecorded={onRecorded}
        />
      ))}
    </div>
  );
};
